import * as React from "react";
import { useCallback, useState } from "react";
import { useGetProductsQuery } from "../../services/product";
import { Button } from "../../components/ui/button";
import { Calendar as CalendarComponent } from "../../components/ui/calendar";
import LoadingState from "../../components/states/LoadingState";
import ErrorState from "../../components/states/ErrorState";
import { PRODUCTS_PAGE_URL } from "../../../constants";
import ProductList from "../../components/products/ProductList";
import { ChevronDown } from "lucide-react";

export const ProductsPage = () => {
  const allPlatforms = ["facebook", "pinterest"];
  const [selectedPlatforms, setSelectedPlatforms] = useState(allPlatforms);
  const [dateRange, setDateRange] = useState("allTime");
  const [customRange, setCustomRange] = useState({ from: null, to: null });
  const [activeFilters, setActiveFilters] = useState(() => ({
    platform: "all",
    start_date: null,
    end_date: null,
  }));
  const [isManualRefetch, setIsManualRefetch] = useState(false);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  // Calculate API filters
  const getApiFilters = useCallback(() => {
    let apiFilters = {};

    if (
      selectedPlatforms.length === 0 ||
      selectedPlatforms.length === allPlatforms.length
    ) {
      apiFilters.platform = "all";
    } else {
      apiFilters.platform = selectedPlatforms[0] === "facebook" ? "fb" : "pin";
    }

    if (dateRange !== "allTime" && customRange.from) {
      const formatDate = (date) => {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
          2,
          "0"
        )}-${String(d.getDate()).padStart(2, "0")}`;
      };

      apiFilters.start_date = formatDate(customRange.from);
      if (customRange.to) {
        apiFilters.end_date = formatDate(customRange.to);
      }
    }
    return apiFilters;
  }, [allPlatforms.length, selectedPlatforms, dateRange, customRange]);

  // right now, when we change the filters, the data gets refetched. giving refetchOnMountOrArgChange: false doesn't prevent it
  // so, I am gonna use another state variable "activeFilters", to prevent the unnecessary refetches
  // directly call the params: getApiFilters(), if this functionality is needed in the future

  const {
    data: allProducts,
    error,
    isLoading,
    refetch,
    isFetching,
  } = useGetProductsQuery(
    { url: PRODUCTS_PAGE_URL, params: activeFilters },
    {
      refetchOnFocus: false,
    }
  );

  // Add check for access denied response
  const isAccessDenied =
    allProducts &&
    "Error" in allProducts &&
    allProducts.alert_banner === "access denied";

  // Calculate date range based on selection
  const calculateDateRange = (rangeType) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const ranges = {
      today: {
        from: today + 1,
        to: today + 1,
      },
      yesterday: {
        from: new Date(today.setDate(today.getDate() - 1)),
        to: new Date(today),
      },
      "Last 7 Days": {
        from: new Date(new Date(today).setDate(today.getDate() - 6)),
        to: new Date(today),
      },
      "1 Month": {
        from: new Date(today.getFullYear(), today.getMonth(), 1),
        to: new Date(today.getFullYear(), today.getMonth() + 1, 0),
      },
      allTime: { from: null, to: null },
    };
    return ranges[rangeType] || { from: null, to: null };
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    if (range !== "custom") {
      setCustomRange(calculateDateRange(range));
    }
  };

  const handleCalendarSelect = (range) => {
    setCustomRange(range || { from: null, to: null });
    setDateRange("custom");
  };

  const handleApplyFilters = async () => {
    setIsManualRefetch(true);
    setActiveFilters(getApiFilters());
    await refetch();
    setIsManualRefetch(false);
  };

  if (error) return <ErrorState error={error.message || "An error occurred"} />;
  if (isLoading || isManualRefetch) return <LoadingState />;

  if (isAccessDenied)
    return (
      <ErrorState
        title="Access Denied"
        error={allProducts.Error}
        description="You do not have permission to access this page."
        backgroundColor={allProducts.bg_color}
      />
    );

  return (
    <div className="space-y-4 w-full">
      <div style={{ backgroundColor: allProducts[0]?.Color }} className="p-2">
        <h1 className="text-2xl font-bold text-gray-800">
          {allProducts[0]?.title}
        </h1>
        <h2 className="text-lg font-medium text-gray-800/75">
          {allProducts[0]["sub-title"]}
        </h2>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-y-auto">
        <button
          onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <span className="font-medium text-gray-700">Filters</span>
          <ChevronDown
            className={`h-5 w-5 text-gray-500 transition-transform ${
              isFiltersExpanded ? "rotate-180" : ""
            }`}
          />
        </button>

        {/* Collapsible Content */}
        <div
          className={`transition-all duration-300 ease-in-out origin-top overflow-hidden ${
            isFiltersExpanded
              ? "max-h-[2000px] opacity-100"
              : "max-h-0 opacity-0"
          }`}
          style={{
            transformOrigin: "top",
            transitionProperty: "transform, opacity",
          }}
        >
          <div className="p-4 border-t border-gray-200">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Platform Selection */}
              <div className="flex-1">
                <h3 className="font-medium mb-2">Platforms</h3>
                <div className="flex flex-wrap gap-2">
                  {allPlatforms.map((platform) => (
                    <label
                      key={platform}
                      className="inline-flex items-center p-2 text-sm sm:text-xs bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200"
                    >
                      <input
                        type="checkbox"
                        checked={selectedPlatforms.includes(platform)}
                        onChange={() => {
                          setSelectedPlatforms((prev) =>
                            prev.includes(platform)
                              ? prev.filter((p) => p !== platform)
                              : [...prev, platform]
                          );
                        }}
                        className="mr-2"
                      />
                      <span className="capitalize">{platform}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Date Range Selection */}
              <div className="flex-1">
                <h3 className="font-medium mb-2 text-nowrap">Date Range</h3>
                <div className="flex flex-col lg:flex-row gap-4">
                  {/* Range Options */}
                  <div className="flex flex-col gap-2 min-w-[100px] shrink-0">
                    {[
                      "allTime",
                      "today",
                      "yesterday",
                      "Last 7 Days",
                      "1 Month",
                      "custom",
                    ].map((range) => (
                      <label
                        key={range}
                        className="inline-flex items-center p-2 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200"
                      >
                        <input
                          type="radio"
                          checked={dateRange === range}
                          onChange={() => handleDateRangeChange(range)}
                          className="mr-2"
                        />
                        <span className="capitalize">
                          {range === "allTime" ? "All Time" : range}
                        </span>
                      </label>
                    ))}
                  </div>

                  {/* Calendar */}
                  <div className="flex-1 overflow-x-auto">
                    <CalendarComponent
                      mode="range"
                      selected={customRange}
                      onSelect={handleCalendarSelect}
                      className="rounded-md justify-around xl:justify-start border w-full"
                      numberOfMonths={2}
                      classNames={{
                        months: " justify-around",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Apply Button */}
            <div className="flex justify-end mt-4">
              <Button
                onClick={handleApplyFilters}
                disabled={isLoading || isFetching}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ProductList allProducts={allProducts[1]} />
    </div>
  );
};

export default ProductsPage;
